import { exerciseNow, setExerciseTimeout, clearExerciseTimeout } from "../exercises/pause-clock";
import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useMemo, useState } from "react";
import { ZEITFORMEN_KONJUGIEREN_PERSONEN, type Zeitform, type ZeitformenKonjugierenExerciseConfig, type ZeitformenKonjugierenRunSettings } from "@calega/content";
import type { ZeitformenKonjugierenRun } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { useRealtime } from "../realtime/RealtimeContext";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { ExerciseDurationSelect } from "./ExerciseSettings";
import { abortExerciseRun, submitExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";

type Phase = "running" | "feedback" | "complete";
type Answer = { itemId: string; values: string[]; rowCorrect: boolean[]; correct: boolean };
type State = { result?: { score: number; maxScore: number }; runId: string; run: ZeitformenKonjugierenRun; index: number; phase: Phase; endsAt: number | null; values: string[]; answers: Answer[] };

const constructionPlans: Record<Zeitform, string> = {
  praesens: "Personalform des Verbs",
  praeteritum: "Vergangenheitsform als einteiliges Verb",
  perfekt: "Hilfsverb + Partizip II",
  plusquamperfekt: "hatte/war + Partizip II",
  futur1: "werden + Infinitiv",
  futur2: "werden + Partizip II + haben/sein",
};

function emptyValues() { return ["", "", "", "", "", ""]; }
function capitalize(value: string) { return value ? value[0]!.toLocaleUpperCase("de-DE") + value.slice(1) : value; }

export function ZeitformenKonjugieren({ account, config, pageId }: { account: Account; config: ZeitformenKonjugierenExerciseConfig; pageId: string }) {
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const syncFollower = Boolean(realtime.session && !facilitator);
  const canControl = !realtime.session || facilitator;
  const canAnswer = !realtime.session || facilitator || syncFollower;
  const [mode, setMode] = useState<ZeitformenKonjugierenRunSettings["mode"]>(config.defaultMode);
  const [count, setCount] = useState(config.defaultCount);
  const [durationSeconds, setDurationSeconds] = useState(config.defaultDurationSeconds);
  const [tenses, setTenses] = useState<Zeitform[]>(config.defaultTenses);
  const [exercise, setExercise] = useState<State | null>(null);
  useLearningDisplayItem(exercise?.runId, exercise?.index ?? 0, Boolean(exercise && exercise.phase !== "complete"));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [, setClockTick] = useState(0);
  const labels = useMemo(() => new Map(config.tenses.map(({ id, label }) => [id, label])), [config.tenses]);

  function publish(next: State) { realtime.publishExercise({ exerciseType: "zeitformen-konjugieren", pageId, state: next }); }
  function publishReset() { realtime.publishExercise({ exerciseType: "zeitformen-konjugieren", pageId, state: null }); }
  function settings(): ZeitformenKonjugierenRunSettings { return { mode, count, durationSeconds, tenses }; }
  function setFromSettings(next: ZeitformenKonjugierenRunSettings) { setMode(next.mode); setCount(next.count); setDurationSeconds(next.durationSeconds); setTenses(next.tenses); }

  useEffect(() => {
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: State | null } | null;
    if (synced?.exerciseType !== "zeitformen-konjugieren" || synced.pageId !== pageId) return;
    setExercise(synced.state ?? null);
  }, [realtime.state.exercise, pageId]);

  useEffect(() => {
    if (!exercise?.endsAt || exercise.phase === "complete") return;
    const timer = window.setInterval(() => setClockTick((value) => value + 1), 250);
    return () => window.clearInterval(timer);
  }, [exercise?.endsAt, exercise?.phase]);

  useEffect(() => {
    if (busy || !exercise?.endsAt || exercise.phase === "complete" || !canControl) return;
    const timer = setExerciseTimeout(() => finish().catch((error) => setMessage(error.message)), Math.max(0, exercise.endsAt - exerciseNow(realtime.serverNow)) + 20);
    return () => clearExerciseTimeout(timer);
  }, [exercise?.endsAt, exercise?.phase, exercise?.index, exercise?.answers.length, canControl, busy]);

  useEffect(() => {
    const answer = exercise?.answers.at(-1);
    if (!exercise || exercise.phase !== "feedback" || !answer?.correct || !canControl) return;
    const timer = setExerciseTimeout(() => next().catch((error) => setMessage(error.message)), 700);
    return () => clearExerciseTimeout(timer);
  }, [exercise?.phase, exercise?.index, exercise?.answers.length, canControl, busy]);

  async function start(nextSettings = settings()) {
    if (busy || !canControl || nextSettings.tenses.length < 1) return;
    setBusy(true);
    setMessage("Durchlauf wird vorbereitet …");
    try {
      const result = await startExerciseRun<{ runId: string; exercise: ZeitformenKonjugierenRun }>({ contentPageId: pageId, settings: nextSettings });
      const next: State = { runId: result.runId, run: result.exercise, index: 0, phase: "running", endsAt: result.exercise.settings.mode === "time" ? exerciseNow(realtime.serverNow) + result.exercise.settings.durationSeconds * 1_000 : null, values: emptyValues(), answers: [] };
      setExercise(next);
      setMessage("");
      publish(next);
    } finally { setBusy(false); }
  }

  async function abortCurrentRun() { if (exercise && exercise.phase !== "complete") await abortExerciseRun(exercise.runId); }
  async function backToSettings() { if (!exercise || busy || !canControl) return; setBusy(true); try { await abortCurrentRun(); setFromSettings(exercise.run.settings); setExercise(null); setMessage(""); publishReset(); } finally { setBusy(false); } }
  async function remix() { if (!exercise || busy || !canControl) return; const nextSettings = exercise.run.settings; setBusy(true); try { await abortCurrentRun(); } finally { setBusy(false); } await start(nextSettings); }

  function updateValue(index: number, value: string) {
    if (!exercise || exercise.phase !== "running" || !canAnswer) return;
    const values = exercise.values.map((current, currentIndex) => currentIndex === index ? value : current);
    const next = { ...exercise, values };
    setExercise(next);
    publish(next);
  }

  async function answer() {
    if (!exercise || exercise.phase !== "running" || busy || !canAnswer) return;
    const item = exercise.run.items[exercise.index];
    if (!item) return;
    setBusy(true);
    try {
      const result = await submitExerciseAnswer<{ rowCorrect: boolean[] }>(exercise.runId, { type: "zeitformen-konjugieren", itemId: item.id, values: exercise.values });
      const rowCorrect = result.rowCorrect;
      const next: State = { ...exercise, phase: "feedback", answers: [...exercise.answers, { itemId: item.id, values: exercise.values, rowCorrect, correct: rowCorrect.every(Boolean) }] };
      setExercise(next);
      publish(next);
    } finally { setBusy(false); }
  }

  async function next() {
    if (!exercise || exercise.phase !== "feedback" || busy || !canControl) return;
    if ((exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length) return finish();
    const nextState = { ...exercise, index: exercise.index + 1, phase: "running" as const, values: emptyValues() };
    setExercise(nextState);
    publish(nextState);
  }

  async function finish() {
    if (!exercise || exercise.phase === "complete" || busy || !canControl) return;
    await completion.complete(exercise.runId, (result) => {
      const next = { ...exercise, result, phase: "complete" as const };
      setExercise(next);
      publish(next);
      setMessage("");
    });
  }
  async function closeCompletedRun(repeat: boolean) { if (!exercise || exercise.phase !== "complete") return; if (repeat) await remix(); else await backToSettings(); }

  function toggleTense(tense: Zeitform) { setTenses((current) => current.includes(tense) ? current.filter((id) => id !== tense) : [...current, tense]); }

  if (syncFollower && !exercise) return <ExerciseWaitingCard className="konjugation-exercise" />;

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (!exercise) return <section className="konjugation-exercise">
    <p className="quiz-instructions learning-text">{config.instructions}</p>
    <div className="konjugation-settings">
      <fieldset><legend>Zeitformen</legend><div className="blitz-choice-row">{config.tenses.map((tense) => <label className="checkbox-label" key={tense.id}><input type="checkbox" disabled={!canControl || busy} checked={tenses.includes(tense.id)} onChange={() => toggleTense(tense.id)} />{tense.label}</label>)}</div></fieldset>
      <fieldset><legend>Durchlauf</legend><div className="blitz-choice-row"><label className="checkbox-label"><input type="radio" disabled={!canControl || busy} name={`${pageId}-konjugation-modus`} checked={mode === "count"} onChange={() => setMode("count")} />Feste Anzahl</label><label className="checkbox-label"><input type="radio" disabled={!canControl || busy} name={`${pageId}-konjugation-modus`} checked={mode === "time"} onChange={() => setMode("time")} />Auf Zeit</label></div></fieldset>
      {mode === "count" ? <label>Aufgabenanzahl<select disabled={!canControl || busy} value={count} onChange={(event) => setCount(Number(event.target.value))}>{[5, 10, 15, 20].map((value) => <option value={value} key={value}>{value}</option>)}</select></label> : <ExerciseDurationSelect disabled={!canControl || busy} value={durationSeconds} onChange={setDurationSeconds} />}
    </div>
    <button className="primary" disabled={busy || !canControl || tenses.length < 1} onClick={() => start().catch((error) => setMessage(error.message))}>Konjugation starten</button>
    {tenses.length < 1 && <div className="alert info">Bitte mindestens eine Zeitform auswählen.</div>}
    {message && <div className="alert info">{message}</div>}
  </section>;

  const item = exercise.run.items[exercise.index];
  const lastAnswer = exercise.answers.at(-1);
  const score = exercise.answers.filter((answer) => answer.correct).length;
  const timeRemaining = exercise.endsAt ? Math.max(0, Math.ceil((exercise.endsAt - exerciseNow(realtime.serverNow)) / 1_000)) : null;
  const last = (exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length;
  return <section className="konjugation-exercise">
    {canControl && exercise.phase !== "complete" && <ExerciseMenu><button className="secondary small" disabled={busy} onClick={() => backToSettings().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button><button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button></ExerciseMenu>}
    <div className="exercise-progress">{exercise.run.settings.mode === "time" ? `${exercise.answers.length} beantwortet · ${timeRemaining ?? 0} s` : `Aufgabe ${Math.min(exercise.index + 1, exercise.run.items.length)} von ${exercise.run.items.length}`}</div>
    {exercise.phase === "complete" ? <div className="reime-result"><strong>{exercise.result?.score ?? score} von {exercise.result?.maxScore ?? exercise.answers.length} richtig</strong><p>Der Durchlauf ist abgeschlossen.</p></div> : item && <>
      <div className="konjugation-card">
        <div className="konjugation-heading learning-text"><div><span>Verb</span><strong>{capitalize(item.infinitive)}</strong></div><div><span>Zeitform</span><strong>{labels.get(item.tense) ?? item.tense}</strong></div></div>
        <details className="konjugation-plan"><summary>Bauplan anzeigen</summary><p className="learning-text">{labels.get(item.tense) ?? item.tense}: {constructionPlans[item.tense]}</p></details>
        <table className="konjugation-table learning-text"><tbody>{ZEITFORMEN_KONJUGIEREN_PERSONEN.map((person, index) => <tr key={person.id}><th scope="row">{person.label}</th><td><input className={exercise.phase === "feedback" ? (lastAnswer?.rowCorrect[index] ? "is-correct" : "is-wrong") : ""} value={exercise.values[index] ?? ""} disabled={exercise.phase !== "running" || busy || !canAnswer} autoComplete="off" spellCheck={false} aria-label={`Verbform für ${person.label}`} onChange={(event) => updateValue(index, event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") answer().catch((error) => setMessage(error.message)); }} /></td></tr>)}</tbody></table>
      </div>
      {exercise.phase === "feedback" && lastAnswer && <div className={`answer-feedback learning-text ${lastAnswer.correct ? "correct" : "wrong"}`}>{lastAnswer.correct ? "Richtig! Alle Formen stimmen." : <><strong>Nicht ganz.</strong><span>Vergleiche deine Formen mit den Lösungen.</span><ul>{ZEITFORMEN_KONJUGIEREN_PERSONEN.map((person, index) => !lastAnswer.rowCorrect[index] && <li key={person.id}><strong>{person.label}:</strong> {item.solutions[index]}</li>)}</ul></>}</div>}
      <div className="konjugation-actions"><button className="primary" disabled={exercise.phase !== "running" || busy || !canAnswer} onClick={() => answer().catch((error) => setMessage(error.message))}>Überprüfen</button>{exercise.phase === "feedback" && !lastAnswer?.correct && canControl && <button className="secondary" disabled={busy} onClick={() => next().catch((error) => setMessage(error.message))}>{last ? "Auswertung ansehen" : "Nächste Aufgabe"}</button>}</div>
    </>}
    {canControl && exercise.phase === "complete" && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}
    {exercise.phase === "complete" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}
    {message && <div className="alert info">{message}</div>}
  </section>;
}
